import React, { useEffect, useState } from 'react';
import axiosInstance from '../../api/api';
import { Dog, ChatCircleDots, Lifebuoy, TrendUp } from '@phosphor-icons/react';

const BuddyInsightsCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axiosInstance.get('/buddy/insights?days=30')
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;
  const maxTopic = Math.max(1, ...(data.topics || []).map(t => t.count));

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] mt-6" data-testid="buddy-insights-card">
      <div className="flex items-center gap-2 mb-1">
        <Dog size={18} weight="fill" className="text-amber-500" />
        <h3 className="font-black uppercase tracking-wide text-sm text-neutral-900 dark:text-white">Buddy Insights</h3>
        <span className="text-[10px] font-bold text-neutral-400 ml-auto">Last {data.days} days</span>
      </div>
      <p className="text-xs text-neutral-500 mb-4">What visitors ask Buddy most — spot what to improve or explain better.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="border-2 border-black p-2.5 bg-amber-50 dark:bg-amber-900/20" data-testid="buddy-insights-questions">
          <p className="text-[10px] font-bold uppercase text-neutral-500">Questions</p>
          <p className="text-2xl font-black">{data.total_questions}</p>
        </div>
        <div className="border-2 border-black p-2.5">
          <p className="text-[10px] font-bold uppercase text-neutral-500">Conversations</p>
          <p className="text-2xl font-black">{data.conversations}</p>
        </div>
        <div className="border-2 border-black p-2.5">
          <p className="text-[10px] font-bold uppercase text-neutral-500">Last 24h</p>
          <p className="text-2xl font-black">{data.questions_24h}</p>
        </div>
        <div className="border-2 border-black p-2.5">
          <p className="text-[10px] font-bold uppercase text-neutral-500 flex items-center gap-1"><Lifebuoy size={12} weight="bold" /> Handoffs</p>
          <p className="text-2xl font-black">{data.handoffs}</p>
        </div>
      </div>

      {data.topics?.length > 0 && (
        <div className="mb-4" data-testid="buddy-insights-topics">
          <p className="text-xs font-black uppercase text-neutral-500 mb-2 flex items-center gap-1"><TrendUp size={14} weight="bold" /> Top Topics</p>
          <div className="space-y-1.5">
            {data.topics.slice(0, 6).map((t) => (
              <div key={t.topic} className="flex items-center gap-2">
                <span className="text-xs font-bold w-36 flex-shrink-0 truncate">{t.topic}</span>
                <div className="flex-1 bg-neutral-100 dark:bg-neutral-700 h-4 border border-black">
                  <div className="h-full bg-amber-400" style={{ width: `${(t.count / maxTopic) * 100}%` }} />
                </div>
                <span className="text-xs font-black w-8 text-right">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.recent_questions?.length > 0 ? (
        <div data-testid="buddy-insights-recent">
          <p className="text-xs font-black uppercase text-neutral-500 mb-2 flex items-center gap-1"><ChatCircleDots size={14} weight="bold" /> Recent Questions</p>
          <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
            {data.recent_questions.map((q, i) => (
              <div key={i} className="text-xs border border-neutral-200 dark:border-neutral-700 p-2 bg-neutral-50 dark:bg-neutral-900/40">
                <span className="text-neutral-800 dark:text-neutral-200">{q.question}</span>
                <span className="text-neutral-400 ml-2">{q.at ? new Date(q.at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-neutral-400" data-testid="buddy-insights-empty">No questions yet — Buddy is waiting for his first visitor. 🐾</p>
      )}
    </div>
  );
};

export default BuddyInsightsCard;
